import React, { useState } from 'react';
import InputMask from 'react-input-mask';
import PropTypes from 'prop-types';
import './MaskedInput.scss';
import InputControl from '../InputControl/InputControl';

/**
 * For further guidance on the MaskedInput component, please visit the
   <a target="_blank" href="http://hqcontent.edwardjones.com/GUI/guisite/axiom-ds/components/form/text-input" class="axiom-link"> AXIOM</a>
    documentation site.
 <br/>
 <br/>
 * The available preset masks using the 'maskType' prop are the following: phone, date (USA, CA),
 * sin (CA), ssn (USA)
 *
 * When passing the 'maskType' prop with a value of date, you must also provide the 'locale'
 * prop with a value of either CA or US.
 *
 * If you need a custom masking not provided, you can pass the mask using the 'customMask' prop.
 * Simple custom masks can be defined as strings.
 * The character '9' allows all integers 0-1.
 * The character 'a' allows all letters a-z, A-Z.
 * The character '*' allows both integers and letters.
 * So for example, the string passed for a SSN mask would be '999-99-9999'.
 * The format characters (9, a, *) can be escaped by a backslash if needed.
 * **PLEASE NOTE: It was previously documented that you could provide custom masks with
 * regular expressions, but this was not correct. Only strings using the above
 * characters will work as expected.
 *
 * If you provide a customMask, you must also provide a customMaskHelperText to
 * show the user the expected format.
 *
 * If the 'inputRef' is not working as expected, try passing a callback function. In a future AXIOM
 * version we will address this issue, most likely be limiting the prop-type to one type.
 */

const MaskedInput = ({
  id,
  classes: modifierClasses,
  label,
  name,
  value,
  labelModifier,
  disabled,
  readOnly,
  error,
  customMaskHelperText,
  onChange,
  onKeyDown,
  customMask,
  maskType,
  inputRef,
  locale,
  onBlur,
  hideLabel,
  onFocus,
  ariaLabel,
  condensed,
}) => {
  const [isFocusOut, setIsFocusOut] = useState(true);

  const [inputValue, setInputValue] = useState(value);
  const formatUserInput = (userInput) => {
    let formattedValue = userInput;
    if (formattedValue !== undefined && maskType === 'phone') {
      formattedValue = userInput.split('-').join('');
    }
    return formattedValue;
  };

  const handleOnChange = (e) => {
    setInputValue(e.target.value);
    if (onChange) {
      onChange(formatUserInput(e.target.value));
    }
  };

  const handleOnBlur = (e) => {
    setIsFocusOut(true);
    setInputValue(e.target.value);
    if (onBlur) {
      onBlur(formatUserInput(e.target.value));
      return;
    }
    if (onChange) {
      onChange(formatUserInput(e.target.value));
    }
  };

  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
    setIsFocusOut(false);
  };

  let maskFormat;
  let helperText;
  let tempValue = value;
  let formatChars = {
    '9': '[0-9]',
    'a': '[A-Za-z]',
    '*': '[A-Za-z0-9]',
  };

  if (customMask) {
    maskFormat = customMask;
    helperText = customMaskHelperText;
  }

  if (maskType === 'phone') {
    maskFormat = '999-999-9999';
    helperText = 'Format as 123-456-7890';
  }

  if (maskType === 'ssn') {
    maskFormat = '999-99-9999'; // TS says this is useless
    helperText = 'Format entry as 123-45-6789';
    /*
    below logic is to,
    (if) hide first 5 numbers while focus-out from input.
    (else) show all numbers while focus-in to input.
    */
    if (
      inputValue &&
      inputValue.split('-').length === 3 &&
      isFocusOut
    ) {
      maskFormat = 'XXX-XX-9999';
      tempValue = `XXX-XX-${inputValue.split('-')[2]}`;
    } else {
      maskFormat = '999-99-9999';
      tempValue = inputValue;
    }
  }

  if (maskType === 'sin') {
    maskFormat = '999-999-999'; // TS says this is useless
    helperText = 'Format entry as 123-456-789';
    /*
    below logic is to,
    (if) hide first 6 numbers while focus-out from input.
    (else) show all numbers while focus-in to input.
    */
    if (
      inputValue &&
      inputValue.split('-').length === 3 &&
      isFocusOut
    ) {
      maskFormat = 'XXX-XXX-999';
      tempValue = `XXX-XX-${inputValue.split('-')[2]}`;
    } else {
      maskFormat = '999-999-999';
      tempValue = inputValue;
    }
  }

  if (maskType === 'date') {
    formatChars = {
      1: '[0-1]',
      2: '[0-2]',
      3: '[0-3]',
      9: '[0-9]',
    };

    if (locale === 'US' || locale === 'en-us') {
      maskFormat = '19/39/2999';
      const yearFormat = `y\u200by\u200by\u200by`;
      helperText = `Format entry as mm/dd/${yearFormat}`;
    }

    if (locale === 'CA' || locale === 'en-ca') {
      maskFormat = '39/19/2999';

      const yearFormat = `y\u200by\u200by\u200by`;
      helperText = `Format entry as dd/mm/${yearFormat}`;
    }
  }

  const setInputRef = (el) => {
    if (!inputRef) return;
    inputRef.current = el;
  };

  const normalizedPropClasses =
    modifierClasses instanceof Array
      ? modifierClasses
      : [modifierClasses];

  const controllerClasses = [
    'axiom-masked-input',
    ...normalizedPropClasses,
  ];

  const wrapperClasses = condensed
    ? `axiom-masked-input--input-wrap--condensed input-control--input--wrap--condensed`
    : 'axiom-masked-input--input-wrap input-control--input--wrap';

  const inputClasses = readOnly
    ? 'axiom-masked-input--input read-only'
    : 'axiom-masked-input--input';

  return (
    <InputControl
      id={id}
      classes={controllerClasses}
      label={label}
      labelModifier={labelModifier}
      error={error}
      microcopy={helperText}
      hideLabel={hideLabel}
      ariaLabel={ariaLabel}
      condensed={condensed}
    >
      <div data-testid='wrap' className={wrapperClasses}>
        <InputMask
          id={id}
          data-testid='maskedInput'
          name={name}
          value={tempValue}
          formatChars={formatChars}
          mask={maskFormat}
          maskChar=''
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          onFocus={handleFocus}
          onKeyDown={onKeyDown}
          disabled={disabled}
          aria-required={labelModifier === 'required'}
          aria-invalid={error ? true : null}
          aria-describedby={
            error || maskType || customMaskHelperText
              ? `${id}_messages`
              : null
          }
          className={inputClasses}
          readOnly={readOnly}
          alwaysShowMask={false}
          inputRef={setInputRef}
        />
      </div>
    </InputControl>
  );
};

MaskedInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  labelModifier: PropTypes.oneOf(['required', 'optional']),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.string,
  customMask: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  customMaskHelperText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  maskType: PropTypes.oneOf(['date', 'phone', 'ssn', 'sin']),
  locale: PropTypes.oneOf(['CA', 'US', 'en-ca', 'en-us']),
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  hideLabel: PropTypes.bool,
  ariaLabel: PropTypes.string,
  condensed: PropTypes.bool,
  classes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

MaskedInput.defaultProps = {
  labelModifier: undefined,
  disabled: false,
  readOnly: false,
  error: undefined,
  customMaskHelperText: undefined,
  customMask: undefined,
  maskType: undefined,
  inputRef: undefined,
  locale: undefined,
  onBlur: undefined,
  onFocus: undefined,
  onKeyDown: undefined,
  hideLabel: false,
  ariaLabel: undefined,
  condensed: false,
  classes: [],
};

export default MaskedInput;
